import { ArrowDownRight, ArrowUpRight } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useIsMobile } from "@/hooks/use-mobile";
import { formatDateWIB } from "@/lib/date-utils";
import { formatCurrency } from "@/lib/utils";

import {
  describeStockCardEntry,
  STOCK_CARD_TIME_FORMAT,
} from "./stock-card-utils";

import type { StockCardEntry } from "@artisancode/api-types";

interface Props {
  entries: StockCardEntry[];
  uomName: string;
}

export function StockCardEntries({ entries, uomName }: Props) {
  const isMobile = useIsMobile();
  return isMobile ? (
    <div className="flex flex-col gap-3 py-3">
      {entries.map((entry) => (
        <StockCardEntryCard key={entry.id} entry={entry} uomName={uomName} />
      ))}
    </div>
  ) : (
    <Table>
      <TableHeader className="sticky top-0 z-10 bg-background shadow-xs">
        <TableRow>
          <TableHead className="w-[140px]">Waktu</TableHead>
          <TableHead>Tipe & Referensi</TableHead>
          <TableHead>Alur Bucket</TableHead>
          <TableHead className="text-right">Masuk</TableHead>
          <TableHead className="text-right">Keluar</TableHead>
          <TableHead className="text-right">Saldo</TableHead>
          <TableHead className="text-right">HPP / Unit</TableHead>
          <TableHead>Keterangan / Alasan</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {entries.map((entry) => (
          <StockCardEntryRow key={entry.id} entry={entry} uomName={uomName} />
        ))}
      </TableBody>
    </Table>
  );
}

function QtyIn({ qty }: { qty: number }) {
  return (
    <span className="inline-flex items-center gap-0.5 text-emerald-600 dark:text-emerald-400">
      <ArrowUpRight className="h-3.5 w-3.5" />+{qty.toLocaleString("id-ID")}
    </span>
  );
}

function QtyOut({ qty }: { qty: number }) {
  return (
    <span className="inline-flex items-center gap-0.5 text-rose-600 dark:text-rose-400">
      <ArrowDownRight className="h-3.5 w-3.5" />-{qty.toLocaleString("id-ID")}
    </span>
  );
}

function StockCardEntryCard({
  entry,
  uomName,
}: {
  entry: StockCardEntry;
  uomName: string;
}) {
  const { refLabel, fromLabel, toLabel } = describeStockCardEntry(entry);

  return (
    <Card className="gap-2 py-3 shadow-xs">
      <CardContent className="space-y-2 px-4 text-xs">
        <div className="flex items-start justify-between gap-2">
          <div>
            <span className="text-sm font-semibold text-foreground">
              {refLabel}
            </span>
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              {formatDateWIB(entry.date, STOCK_CARD_TIME_FORMAT)}
              {entry.createdByName && ` • ${entry.createdByName}`}
            </p>
          </div>
          <div className="text-right font-bold">
            {entry.qtyIn > 0 && <QtyIn qty={entry.qtyIn} />}
            {entry.qtyOut > 0 && <QtyOut qty={entry.qtyOut} />}
            {entry.qtyIn === 0 && entry.qtyOut === 0 && (
              <span className="font-mono font-normal text-muted-foreground">
                -
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-border/40 pt-1 text-xs">
          <span className="text-muted-foreground">Alur:</span>
          <div className="font-medium">
            <span className="text-muted-foreground">{fromLabel}</span>
            <span className="mx-1 text-primary">→</span>
            <span className="text-foreground">{toLabel}</span>
          </div>
        </div>

        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Saldo Berjalan:</span>
          <span className="font-mono font-bold text-foreground">
            {entry.runningBalance.toLocaleString("id-ID")} {uomName}
          </span>
        </div>

        {entry.unitCost ? (
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">HPP / Unit:</span>
            <span className="font-mono text-muted-foreground">
              {formatCurrency(entry.unitCost)}
            </span>
          </div>
        ) : null}

        {(entry.reasonName || entry.note) && (
          <div className="rounded bg-muted/30 p-2 text-[11px] text-muted-foreground">
            {entry.reasonName && (
              <Badge variant="secondary" className="mr-1.5 text-[10px]">
                {entry.reasonName}
              </Badge>
            )}
            <span>{entry.note || "-"}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StockCardEntryRow({
  entry,
  uomName,
}: {
  entry: StockCardEntry;
  uomName: string;
}) {
  const { refLabel, fromLabel, toLabel, isTransfer } =
    describeStockCardEntry(entry);

  return (
    <TableRow className="text-xs">
      <TableCell className="whitespace-nowrap font-medium text-muted-foreground">
        {formatDateWIB(entry.date, STOCK_CARD_TIME_FORMAT)}
      </TableCell>
      <TableCell>
        <div className="font-semibold text-foreground">{refLabel}</div>
        {entry.createdByName && (
          <div className="text-[11px] text-muted-foreground">
            Oleh: {entry.createdByName}
          </div>
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap">
        <span className="text-muted-foreground">{fromLabel}</span>
        <span className="mx-1 text-primary">→</span>
        <span className="font-medium text-foreground">{toLabel}</span>
        {isTransfer && (
          <div className="text-[11px] text-muted-foreground">
            Dipindah: {(entry.qty ?? 0).toLocaleString("id-ID")} {uomName}
          </div>
        )}
      </TableCell>
      <TableCell className="text-right font-medium">
        {entry.qtyIn > 0 ? <QtyIn qty={entry.qtyIn} /> : "-"}
      </TableCell>
      <TableCell className="text-right font-medium">
        {entry.qtyOut > 0 ? <QtyOut qty={entry.qtyOut} /> : "-"}
      </TableCell>
      <TableCell className="text-right font-bold text-foreground">
        {entry.runningBalance.toLocaleString("id-ID")} {uomName}
      </TableCell>
      <TableCell className="text-right text-muted-foreground">
        {entry.unitCost ? formatCurrency(entry.unitCost) : "-"}
      </TableCell>
      <TableCell className="max-w-[200px] truncate">
        {entry.reasonName && (
          <Badge variant="secondary" className="mr-1 text-[10px]">
            {entry.reasonName}
          </Badge>
        )}
        <span className="text-muted-foreground">{entry.note || "-"}</span>
      </TableCell>
    </TableRow>
  );
}
